"use client";

import React, { useState } from "react";
import Link from "next/link";
import type { ComboV3 } from "@/src/types/tenant";
import type { FilterState } from "./SidebarFilter";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { SafeImage } from "@/src/components/SafeImage";
import { useCart } from "@/src/contexts/CartContext";

interface BundleAndSaveProps {
  /** v3 combos from grablocal-service-product. Only source — no legacy fallback. */
  combos?: ComboV3[];
  outletId?: string;
  tenantId?: string;
  prefix?: string;
  filters?: Pick<FilterState, "curators" | "priceRanges">;
  heading?: string;
  subtitle?: string;
}

/**
 * Internal display shape — a thin projection of ComboV3 so the rest of the
 * component doesn't read nested pricing/inventory fields directly.
 */
interface NormalisedCombo {
  _id: string;
  name: string;
  description?: string;
  thumbnail?: string;
  mrp: number;
  sellingPrice: number;
  savings: number;
  tag?: string;
  /** First few member names for the placeholder grid. */
  itemHints: string[];
  /** Total member count. */
  itemCount: number;
  /** Stock; 0 disables add-to-cart. */
  stockQuantity: number;
  addableToCart: boolean;
  /**
   * The combo's OWN outlet, which is not always the page's outlet.
   *
   * `data.outletId` is the tenant's PRIMARY outlet (landing.outlet — lowest
   * outletId), but this rail lists combos from every landing outlet, exactly as
   * the product grid lists products from every outlet. Parampara (tenant 250)
   * runs Raipur as primary and files this combo against Whitefield, so adding it
   * under the page outlet would book the order to the wrong store.
   */
  outletId?: string;
}

function normaliseV3(c: ComboV3): NormalisedCombo {
  const summedMrp = (c.items || []).reduce((acc, it) => acc + (it.mrp || 0) * (it.quantity || 0), 0);
  const savings = c.savings ?? Math.max(0, summedMrp - c.pricing.sellingPrice);
  return {
    _id: c._id,
    name: c.name,
    description: c.description,
    thumbnail: c.thumbnail,
    mrp: c.pricing.mrp,
    sellingPrice: c.pricing.sellingPrice,
    savings,
    tag: c.tag,
    itemHints: (c.items || []).slice(0, 3).map((it) => (it.name || it.sku || "").slice(0, 3).toUpperCase()),
    itemCount: (c.items || []).length,
    stockQuantity: c.inventory?.stockQuantity ?? 0,
    addableToCart: (c.status?.isActive ?? true) && (c.inventory?.stockQuantity ?? 0) > 0,
    outletId: c.outletId,
  };
}

const TAG_STYLES: Record<number, { label: string; color: string }> = {
  0: { label: "Best Seller",  color: "var(--chai-tea)" },
  1: { label: "New Arrivals", color: "var(--chutney-green)" },
  2: { label: "Gifting",      color: "var(--rising-sun)" },
  3: { label: "On Sale",      color: "#F26250" },
};

function matchesPriceRange(price: number, rangeId: string): boolean {
  if (rangeId === "under-300") return price < 300;
  if (rangeId === "300-600") return price >= 300 && price < 600;
  if (rangeId === "600-1000") return price >= 600 && price < 1000;
  if (rangeId === "above-1000") return price >= 1000;
  return false;
}

function matchesComboTag(combo: NormalisedCombo, tagValue: string): boolean {
  if (!combo.tag) return false;
  return combo.tag.toLowerCase() === tagValue.toLowerCase();
}

export const BundleAndSave: React.FC<BundleAndSaveProps> = ({
  combos = [],
  outletId,
  tenantId,
  prefix,
  filters,
  heading,
  subtitle,
}) => {
  const { addCombo } = useCart();
  const [showAll, setShowAll] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const merged = React.useMemo<NormalisedCombo[]>(
    () => combos.map(normaliseV3),
    [combos]
  );

  const filteredCombos = React.useMemo(() => {
    const activeCurators = filters?.curators ?? [];
    const activePriceRanges = filters?.priceRanges ?? [];

    return merged.filter((combo) => {
      if (activePriceRanges.length > 0) {
        const matchesAnyPrice = activePriceRanges.some((range) =>
          matchesPriceRange(combo.sellingPrice, range)
        );
        if (!matchesAnyPrice) return false;
      }
      if (activeCurators.length > 0) {
        const matchesAllTags = activeCurators.every((curator) => matchesComboTag(combo, curator));
        if (!matchesAllTags) return false;
      }
      return true;
    });
  }, [merged, filters]);

  if (filteredCombos.length === 0) return null;

  const visibleCombos = showAll ? filteredCombos : filteredCombos.slice(0, 3);

  const handleAdd = async (combo: NormalisedCombo) => {
    if (!combo.addableToCart) {
      toast.message("This bundle isn't ready for checkout yet");
      return;
    }
    if (!tenantId) {
      toast.error("Missing store context — refresh the page");
      return;
    }
    // The combo's own outlet wins; the page outlet is only a fallback for a
    // combo that somehow arrives without one. Guard the EFFECTIVE outlet, not
    // the page one — a combo carrying its own outlet is addable even when the
    // page has none.
    const effectiveOutletId = combo.outletId || outletId;
    if (!effectiveOutletId) {
      toast.error("Missing outlet — refresh the page");
      return;
    }
    setPendingId(combo._id);
    try {
      await addCombo({ comboId: combo._id, outletId: effectiveOutletId, tenantId, quantity: 1 });
      toast.success(`Added ${combo.name} to cart`);
    } catch (err) {
      toast.error((err as Error)?.message || "Failed to add bundle");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      {/* Section head */}
      <div className="mb-[clamp(16px,2vw,24px)] flex items-end justify-between gap-4">
        <div>
          <h2
            className="leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "clamp(20px,2.6vw,30px)", letterSpacing: "-0.01em" }}
          >
            {heading || "Bundle & save"}
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--techno-black-50)" }}>
            {subtitle || "Curated combos hand-picked by the maker."}
          </p>
        </div>
        {filteredCombos.length > 3 && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="shrink-0 text-sm font-semibold transition cursor-pointer"
            style={{ color: "var(--rising-sun)" }}
          >
            {showAll ? "Show less" : `View all (${filteredCombos.length})`}
          </button>
        )}
      </div>

      {/* Bundle grid — horizontal cards */}
      <div className="grid grid-cols-1 gap-[clamp(14px,2vw,20px)] md:grid-cols-2">
        {visibleCombos.map((combo) => {
          const pending = pendingId === combo._id;
          const cantAdd = !combo.addableToCart;

          return (
            <div
              key={combo._id}
              className="group relative flex overflow-hidden rounded-2xl transition-all cursor-pointer"
              style={{ background: "var(--surface)", border: "1px solid var(--border-soft)", boxShadow: "var(--shadow-soft)" }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "var(--shadow-hover)";
                e.currentTarget.style.borderColor = "var(--border)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "var(--shadow-soft)";
                e.currentTarget.style.borderColor = "var(--border-soft)";
              }}
            >
              {/* Stretched link — see AllProducts for why this is an overlay
                  rather than a wrapper (the add-to-cart control is a <button>).
                  Rendered only when we have a prefix, matching the old guard. */}
              {prefix && (
                <Link
                  href={`/shop/combo/${combo._id}?prefix=${prefix}`}
                  aria-label={combo.name}
                  className="absolute inset-0 z-[1] rounded-2xl"
                />
              )}

              {/* Image — fills (object-cover) */}
              <div className="relative aspect-square w-[40%] max-w-[190px] shrink-0 overflow-hidden" style={{ background: "var(--cream)" }}>
                {combo.thumbnail ? (
                  <SafeImage
                    src={combo.thumbnail}
                    alt={combo.name}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center gap-1.5">
                    {combo.itemHints.map((hint, i) => (
                      <div key={i} className="flex h-12 w-12 items-center justify-center rounded-[10px] text-[11px] font-bold" style={{ background: "rgba(255,255,255,0.65)", color: "var(--navy)" }}>
                        {hint}
                      </div>
                    ))}
                  </div>
                )}
                <span
                  className="absolute left-2.5 top-2.5 inline-block max-w-[calc(100%-1.25rem)] truncate rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.04em] text-white"
                  style={{ background: "var(--coral)" }}
                >
                  Bundle{combo.savings > 0 ? ` · saves ₹${combo.savings}` : ""}
                </span>
              </div>

              {/* Details */}
              <div className="flex min-w-0 flex-1 flex-col p-4">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: "var(--techno-black-50)" }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 01-8 0"/></svg>
                  {combo.itemCount} items
                </div>
                <h3 className="mt-1.5 leading-snug" style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 18, letterSpacing: "-0.01em", color: "var(--techno-black)" }}>
                  {combo.name}
                </h3>
                {combo.description && (
                  <p className="mt-1 leading-snug" style={{ fontSize: "12.5px", color: "var(--techno-black-50)", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {combo.description}
                  </p>
                )}
                <div className="mt-auto flex items-end justify-between gap-2 pt-3.5" style={{ borderTop: "1px dashed var(--border)" }}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-1.5">
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 19, color: "var(--techno-black)" }}>₹{combo.sellingPrice.toLocaleString("en-IN")}</span>
                      {combo.mrp > combo.sellingPrice && (
                        <span className="font-medium" style={{ fontSize: 12, textDecoration: "line-through", color: "var(--techno-black-50)" }}>₹{combo.mrp.toLocaleString("en-IN")}</span>
                      )}
                    </div>
                    {combo.savings > 0 && (
                      <p className="mt-0.5 text-[11px] font-bold" style={{ color: "var(--navy)" }}>Save ₹{combo.savings.toLocaleString("en-IN")}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={pending || cantAdd}
                    onClick={(e) => { e.stopPropagation(); handleAdd(combo); }}
                    className="relative z-[2] inline-flex h-[40px] shrink-0 items-center gap-1.5 rounded-full px-4 text-[13px] font-bold text-white transition active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: "var(--coral)" }}
                    title={cantAdd ? "Bundle not yet available in v3 catalogue" : undefined}
                  >
                    {pending ? "Adding…" : "Add bundle"}
                    {!pending && <Plus size={16} />}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredCombos.length > 3 && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setShowAll((v) => !v)}
            className="flex items-center gap-2 rounded-xl border px-6 py-2.5 text-sm font-semibold transition hover:shadow-sm cursor-pointer"
            style={{
              borderColor: "var(--border)",
              color: "var(--techno-black-70)",
              background: "var(--surface)",
            }}
          >
            {showAll ? (
              <>
                Show less
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15"/>
                </svg>
              </>
            ) : (
              <>
                View all {filteredCombos.length} bundles
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
};
